import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import { useTranslations } from 'use-intl';
import { cn } from '@/lib/cn';

/**
 * `min-h-11` is the floor, and it is here rather than at each call site because
 * the thing that breaks it is not a height — it is `!text-meta`. A screen that
 * shrinks a control's type to fit a dense row drops the line box to about 20px
 * and the control lands at 42, three under the minimum, with no height
 * override anywhere to explain it. Stating the floor once makes the type
 * change safe.
 */
const control =
  'w-full min-h-11 rounded-(--radius-control) border border-line bg-surface px-3 py-2.5 ' +
  'text-base text-ink transition-colors placeholder:text-ink-faint ' +
  'hover:border-line-strong focus:border-ink focus:outline-none ' +
  'aria-[invalid=true]:border-risk';

/**
 * Label, control and message as one unit. The message slot is always rendered so
 * a validation error appearing does not shift the rest of the form downwards.
 */
export function Field({
  label,
  error,
  hint,
  children,
  optional = false,
}: {
  label: string;
  error?: string;
  hint?: string;
  optional?: boolean;
  children: (props: { id: string; invalid: boolean; describedBy?: string }) => ReactNode;
}) {
  const t = useTranslations();
  const id = useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;

  return (
    <div className="grid gap-1.5 pb-2">
      <label htmlFor={id} className="flex items-baseline gap-2 text-meta text-ink-muted">
        <span className="font-medium text-ink">{label}</span>
        {optional ? (
          <span className="text-ink-faint">{t('common.optional')}</span>
        ) : null}
      </label>
      {children({ id, invalid: Boolean(error), describedBy: message ? messageId : undefined })}
      <p
        id={messageId}
        className={`min-h-[1.25rem] text-meta ${error ? 'text-risk' : 'text-ink-faint'}`}
      >
        {message ?? ''}
      </p>
    </div>
  );
}

export function TextInput({
  invalid,
  describedBy,
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean; describedBy?: string }) {
  return (
    <input
      {...rest}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className={cn(control, rest.type === 'number' && 'tabular', className)}
    />
  );
}

export function SelectInput({
  invalid,
  describedBy,
  className = '',
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean; describedBy?: string }) {
  return (
    <select
      {...rest}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className={cn(control, className)}
    >
      {children}
    </select>
  );
}

export function TextArea({
  invalid,
  describedBy,
  className = '',
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean; describedBy?: string }) {
  return (
    <textarea
      {...rest}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      className={cn(control, 'min-h-24 resize-y leading-relaxed', className)}
    />
  );
}
